import * as Sentry from '@sentry/browser';
import { HttpClient as HttpClientIntegration } from '@sentry/integrations';
import { withoutGraphQLFetch } from 'apollo-link-sentry';
import env from 'env/.public-env.json';

// Well-known, non-actionable errors that add noise without signal. Sentry
// matches each entry against the event's message/type. Refine against the
// live top-issues list before trimming.
export const IGNORE_ERRORS = [
  // ResizeObserver loop noise (fires from the non-ag-grid observer sites)
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Network aborts / offline blips — user navigated away or lost connection.
  // Not a code chunk that failed to load ("Failed to fetch dynamically
  // imported module"), which a plain 'Failed to fetch' would also match.
  /^(TypeError: )?Failed to fetch(?! dynamically imported module)/,
  'NetworkError when attempting to fetch resource',
  'Load failed',
  'The operation was aborted',
  'AbortError',
  'The user aborted a request',
  // Sentry-wrapped non-error throws with no stack / no signal
  'Non-Error promise rejection captured',
];

// The error-event type the SDK hands to beforeSend. Derived from the option
// signature because @sentry/browser v7 doesn't re-export the ErrorEvent type
// under the Sentry namespace.
export type BeforeSendEvent = Parameters<
  NonNullable<Sentry.BrowserOptions['beforeSend']>
>[0];

// Extracted so the filtering logic can be unit-tested (see
// src/__tests__/configureSentry.test.ts). Passed by reference into Sentry.init.
export const beforeSend = (
  event: BeforeSendEvent,
  hint: Sentry.EventHint,
): BeforeSendEvent | null => {
  if (window.location.hostname === 'localhost') {
    return null;
  }

  // Firestore listeners without an error callback emit permission-denied when
  // the auth token is revoked mid-listen (sign-out / account-switch). Expected
  // churn, not a bug. Removing this branch temporarily surfaces genuine
  // Firestore rule misconfigurations if one is suspected.
  const original = hint?.originalException as
    | (Error & { code?: string })
    | undefined;
  const originalMessage =
    typeof original === 'object' && original ? original.message ?? '' : '';
  if (
    original?.code === 'permission-denied' ||
    /missing or insufficient permissions/i.test(originalMessage)
  ) {
    return null;
  }

  if (
    hint &&
    hint.originalException &&
    // @ts-ignore
    hint.originalException.graphQLErrors &&
    // @ts-ignore
    hint.originalException.graphQLErrors.length > 0
  ) {
    // @ts-ignore
    const { name, message } = hint.originalException.graphQLErrors[0];
    if (
      name &&
      event.exception &&
      event.exception.values &&
      Array.isArray(event.exception.values) &&
      event.exception.values.length > 0
    ) {
      event.exception.values[0].type = name;
    }
    event.fingerprint = [stripCarrierFromName(name), message];
    return event;
  }

  if (
    hint &&
    hint.originalException &&
    // @ts-ignore
    hint.originalException.networkError &&
    // @ts-ignore
    hint.originalException.networkError.name
  ) {
    // @ts-ignore
    const { name, message } = hint.originalException.networkError;
    if (
      name &&
      event.exception &&
      event.exception.values &&
      Array.isArray(event.exception.values) &&
      event.exception.values.length > 0
    ) {
      event.exception.values[0].type = name;
    }
    event.fingerprint = [stripCarrierFromName(name), message];
    return event;
  }

  return event;
};

// The Apollo onError link (buildApolloClient.ts) shapes error names as
// "{carrierName} - {query}: {code}", which fragments one error into a separate
// Sentry issue per carrier. Strip the carrier prefix so the same error groups
// into a single issue. Names without the " - " separator pass through unchanged.
export const stripCarrierFromName = (name?: string): string => {
  if (!name) {
    return '';
  }
  const separatorIndex = name.indexOf(' - ');
  return separatorIndex === -1 ? name : name.slice(separatorIndex + 3);
};

Sentry.init({
  dsn: 'https://998b8a11f1204d8cb38c5ad38ef411b6@sentry.io/1378793',
  // Calendar version on master deploys; branch channel builds fall back to
  // the pipeline ID. Must match the --release flag in the sentry:sourcemaps
  // script or stack traces stop symbolicating.
  release: process.env.REACT_APP_VERSION || process.env.REACT_APP_BUILD_NUMBER,
  environment: env.SENTRY_ENV,
  maxBreadcrumbs: 30,
  normalizeDepth: 3,
  ignoreErrors: IGNORE_ERRORS,
  integrations: [new Sentry.BrowserTracing(), new HttpClientIntegration()],
  // for Sentry.BrowserTracing
  tracesSampleRate: 0.1,
  // for Sentry.BrowserTracing
  tracePropagationTargets: ['localhost', /^https:\/\/app\.fleetops\.com/],
  beforeBreadcrumb: withoutGraphQLFetch((breadcrumb) => {
    if (
      breadcrumb.category === 'graphql.query' ||
      breadcrumb.category === 'graphql.mutation'
    ) {
      if (breadcrumb.data) {
        breadcrumb.message = JSON.stringify(breadcrumb.data.variables);
      }
    }

    if (
      breadcrumb.category === 'xhr' &&
      breadcrumb.data &&
      breadcrumb.data.url &&
      breadcrumb.data.url.includes('firestore.googleapis')
    ) {
      return null;
    }

    // Keep clicks so an event shows what the user did before it. Other ui.*
    // breadcrumbs (keypresses) are noise.
    if (
      breadcrumb.category &&
      breadcrumb.category.includes('ui.') &&
      breadcrumb.category !== 'ui.click'
    ) {
      return null;
    }

    if (Object.keys(breadcrumb).length === 1) {
      // Remove "Generic" events with no meaningful info
      return null;
    }

    return breadcrumb;
  }),
  beforeSend,
});
